import { InfoMessage } from '@/components/shared/InfoMessage/InfoMessage';
import { Button } from '@/components/ui/Button/Button';
import { DialogBody, DialogFooter } from '@/components/ui/Dialog/Dialog';
import { Label } from '@/components/ui/Label/Label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/RadioGroup/RadioGroup';
import { useCancelAppointment } from '@/features/scheduling/api/cancelAppointment';
import { getSelectedDeliveryCenterSchedulingDetails } from '@/features/scheduling/utils/getSelectedDeliveryCenterSchedulingDetails';
import { invalidateSchedulingQueries } from '@/features/scheduling/utils/invalidateSchedulingQueries';
import { useTimeFor } from '@/hooks/useTimeFor';
import { captureAnalyticsEvent } from '@/lib/mixpanel';
import { isAfter, subHours } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { SessionSummary } from '../../../SessionSummary/SessionSummary';
import { useCancelFlow } from '../../context/useCancelFlow';

const WARNING_WINDOW_HOURS = 24;

export const CancelStep = () => {
  const { data, updateData, goToStep, closeCancelDialog } = useCancelFlow();
  const { t } = useTranslation('scheduling', { keyPrefix: 'cancelDialog.steps.cancelStep' });
  const { formatWithZone, formatDate } = useTimeFor('cancellationDeadline');

  const { mutate: cancelAppointment, isPending } = useCancelAppointment({
    options: {
      onSuccess: () => {
        void captureAnalyticsEvent('cancelling_complete', {
          appointment_id: data.appointment?.appointment_id,
          apply_to_following: data.applyToFollowingSessions,
        });
        invalidateSchedulingQueries();
        goToStep('success');
      },
      onError: (error) => {
        const isLateError = error.errors?.some((err) => err.code === 'APPOINTMENT_IN_LATE_CANCELLATION_WINDOW');

        if (isLateError) {
          goToStep('lateCancelError');
        } else {
          goToStep('error');
        }
      },
    },
  });

  if (!data.appointment) {
    throw new Error('No appointment data found');
  }

  if (!data.config.data) {
    throw new Error('No config data found');
  }

  const appointmentStartTime = data.appointment.appointment_date;

  if (!appointmentStartTime) {
    throw new Error('No appointment start time found');
  }

  const centerDetails = getSelectedDeliveryCenterSchedulingDetails(data.config.data?.students, data.appointment?.student_id, data.appointment?.delivery_type);

  const minWindow = centerDetails?.minimum_cancellation_window_hours ?? 0;
  const freeCancellationUntil = subHours(new Date(appointmentStartTime), minWindow);

  const isLateCancellation = new Date() > freeCancellationUntil;

  const warningStart = subHours(freeCancellationUntil, WARNING_WINDOW_HOURS);
  const shouldShowWarningBanner = isAfter(new Date(), warningStart) || isLateCancellation;

  const warning = isLateCancellation ? (
    <InfoMessage variant='alert' description={t('info.lateCancellation.description')} title={t('info.lateCancellation.title')} />
  ) : (
    <InfoMessage variant='warning' title={t('info.warning.title')}>
      <p className='text-body-m-regular'>
        {t('info.warning.description.beforeDate')}{' '}
        <time dateTime={freeCancellationUntil.toISOString()}>
          <strong>{formatDate(freeCancellationUntil)}</strong> {t('info.warning.description.at')} <strong>{formatWithZone(freeCancellationUntil, 'p')}</strong>
        </time>
        {t('info.warning.description.afterDate')}
      </p>
    </InfoMessage>
  );

  const handleSubmit = () => {
    cancelAppointment({
      appointmentId: data.appointment!.appointment_id,
      studentId: data.appointment!.student_id,
      applyToFollowingAppointments: data.applyToFollowingSessions,
      isLateCancel: isLateCancellation,
    });
  };

  const handleKeepSession = () => {
    closeCancelDialog();
  };

  const handleApplyToFollowingChange = (value: string) => {
    updateData('applyToFollowingSessions', value === 'multiple');
  };

  const appointmentStudentConfig = data.config.data?.students.find((studentConfig) => studentConfig.id === data.appointment?.student_id);

  const appointmentEnrollment = appointmentStudentConfig?.enrollments.find((enrollment) => enrollment.id === data.appointment?.enrollment_id);

  return (
    <>
      <DialogBody>
        <p>{t('title')}</p>
        {shouldShowWarningBanner && warning}
        <SessionSummary appointment={data.appointment} enrollment={appointmentEnrollment} className='mt-2 mb-6' />
        {data.appointment.recurring_schedule && (
          <div className='space-y-3'>
            <p>{t('recurring.title')}</p>
            <RadioGroup value={data.applyToFollowingSessions ? 'multiple' : 'single'} onValueChange={handleApplyToFollowingChange}>
              <div className='bg-fill-background-light flex items-center justify-between rounded-lg px-5 py-3'>
                <Label htmlFor='r1' className='text-body-m-semibold w-full'>
                  {t('recurring.single')}
                </Label>
                <RadioGroupItem value='single' id='r1' />
              </div>
              <div className='bg-fill-background-light flex items-center justify-between rounded-lg px-5 py-3'>
                <Label htmlFor='r2' className='text-body-m-semibold w-full'>
                  {t('recurring.multiple')}
                </Label>
                <RadioGroupItem value='multiple' id='r2' />
              </div>
            </RadioGroup>
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant='text-secondary' className='w-full' onClick={handleKeepSession}>
          {t('backButton.label')}
        </Button>
        <Button className='w-full' onClick={handleSubmit} isLoading={isPending} disabled={isPending}>
          {t('confirmButton.label')}
        </Button>
      </DialogFooter>
    </>
  );
};
